import { EmployeeProductType } from '../../generated/api/employeeProductType.js';
import { DeviceSelectionProductTypeCoCo } from '../enums.js';
import { sortArrayByProperty } from './arrayUtils.js';
import { toTimestamp } from './dateUtilsCoCo.js';
import { FILTER_VALUE_SEPARATOR } from './deviceListTabsUtilsCoCo.js';
import { getDeviceSelectionCategories } from './deviceSelectionCategoryUtilsCoCo.js';
import { toDeviceSelectionVariantDevices } from './deviceSelectionDeviceUtilsCoCo.js';
import { matchesSearch } from './productListUtilsCoCo.js';
import type { DeviceVariantGroupsByTypeCoCo } from './deviceSelectionDeviceUtilsCoCo.js';
import type { EmployeeProductSelectionResponse } from '../../generated/api/employeeProductSelectionResponse.js';

export interface DeviceSelectionListItemCoCo {
  uuid: string;
  name: string;
  productType: DeviceSelectionProductTypeCoCo;
  lastModified?: number;
}

// Column keys are the list item properties, so the list can be sorted by them
export enum DeviceSelectionListColumnCoCo {
  NAME = 'name',
  PRODUCT_TYPE = 'productType',
  LAST_MODIFIED = 'lastModified',
}

// Only EPP selections are Elisa Devices Service selections, the other product types are purchased devices
export const toDeviceSelectionProductType = (productType?: EmployeeProductType): DeviceSelectionProductTypeCoCo =>
  productType === EmployeeProductType.EPP
    ? DeviceSelectionProductTypeCoCo.EPP
    : DeviceSelectionProductTypeCoCo.PURCHASED;

export const getDeviceSelectionItemsByCategory = (
  selection: EmployeeProductSelectionResponse,
  variantGroups: DeviceVariantGroupsByTypeCoCo
) => {
  const devices = toDeviceSelectionVariantDevices(variantGroups, toDeviceSelectionProductType(selection.productType));
  const categoriesByVariantGroupId = new Map(devices.map(device => [device.id, device.category]));

  return getDeviceSelectionCategories(toDeviceSelectionProductType(selection.productType))
    .map(categoryConfig => ({
      categoryConfig,
      items: selection.items.filter(
        ({ variantGroupId }) => categoriesByVariantGroupId.get(variantGroupId) === categoryConfig.category
      ),
    }))
    .filter(({ items }) => items.length > 0);
};

export const toDeviceSelectionListItem = (
  selection: EmployeeProductSelectionResponse
): DeviceSelectionListItemCoCo => ({
  uuid: selection.uuid,
  name: selection.name,
  productType: toDeviceSelectionProductType(selection.productType),
  lastModified: toTimestamp(selection.lastModifiedDate),
});

// Search and product type filter values come from the Device selection tab toolbar
export const filterDeviceSelectionListItems = (
  items: DeviceSelectionListItemCoCo[],
  search = '',
  productTypeFilter = ''
) => {
  const productTypes = productTypeFilter.split(FILTER_VALUE_SEPARATOR).filter(Boolean);
  return items.filter(
    ({ name, productType }) =>
      matchesSearch(search, [name]) && (productTypes.length === 0 || productTypes.includes(productType))
  );
};

// Without a sort param the most recently modified selections are shown first
export const sortDeviceSelectionListItems = (items: DeviceSelectionListItemCoCo[], sort?: string, order?: string) =>
  sort
    ? sortArrayByProperty([...items], sort, order)
    : sortArrayByProperty([...items], DeviceSelectionListColumnCoCo.LAST_MODIFIED, 'desc');
